import { FulfillmentMethod } from "@aitisal/contracts/common";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { createSafeJSONStorage } from "./safeStorage";

interface User {
  userPhone: string;
  userAddress: string;
  userNotes: string;
}

interface SessionState {
  userName: string;
  restaurantId: string;
  brandId: string;
  restaurantAreaId: string;
  selectedAreaId: string | null;
  insideDeliveryArea: boolean;
  distanceFromRestaurant: number;
  locationTimestamp: number | null;
  pointOfOrder: Record<string, any>;
  user: User;
  orderId: string;
  metadata: Record<string, any>;
  isOrderingEnabled: boolean;
  signedInUserDetails: Record<string, any>;
  latitude: number;
  longitude: number;
  personAddress: string;
  savedAddresses: Record<string, any>[];
  isPickup: boolean;
  fullfillmentType: string;
  selectedDeliveryOption: any;
  setUserName: (name: string) => void;
  setSelectedDeliveryOption: (option: any) => void;
  setFullfillmentType: (fullfillmentType: FulfillmentMethod) => void;
  setLatitude: (latitude: number) => void;
  setLongitude: (longitude: number) => void;
  setPersonAddress: (address: string) => void;
  setSavedAddresses: (addresses: Record<string, any>[]) => void;
  setRestaurantId: (id: string) => void;
  setPointOfOrder: (po: Record<string, any>) => void;
  setOrderId: (id: string) => void;
  setUser: (userPhone: string, userAddress: string, userNotes: string) => void;
  setMetadata: (data: Record<string, any>) => void;
  setSignedInUserDetails: (userDetails: Record<string, any>) => void;
  resetUserSession: () => void;
  resetRestaurant: () => void;
  resetLocation: () => void;
  setRestaurantAreaId: (id: string) => void;
  setSelectedAreaId: (id: string | null) => void;
  setInsideDeliveryArea: (inside: boolean) => void;
  setDistanceFromRestaurant: (distance: number) => void;
  setIsOrderingEnabled: (active: boolean) => void;
  setIsPickup: (isPickup: boolean) => void;
  setBrandId: (id: string) => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set) => ({
      restaurantId: "",
      restaurantAreaId: "",
      selectedAreaId: null,
      insideDeliveryArea: false,
      distanceFromRestaurant: 0,
      locationTimestamp: null,
      userName: "",
      pointOfOrder: {},
      user: {
        userPhone: "",
        userAddress: "",
        userNotes: "",
      },
      brandId: "",
      orderId: "",
      metadata: {},
      isUserLoggedIn: false,
      isOrderingEnabled: false,
      signedInUserDetails: {},
      latitude: 0,
      longitude: 0,
      personAddress: "",
      savedAddresses: [],
      isPickup: false,
      fullfillmentType: FulfillmentMethod.BRANCH_DELIVERY,
      selectedDeliveryOption: null,
      setSelectedDeliveryOption: (option: any) => {
        set({ selectedDeliveryOption: option });
      },
      setUserName: (name: string) => {
        set({ userName: name });
      },
      setBrandId: (id: string) => {
        set({ brandId: id });
      },
      setIsPickup: (isPickup: boolean) => {
        set({ isPickup });
      },
      setFullfillmentType: (fullfillmentType: string) => {
        set({ fullfillmentType: fullfillmentType });
      },
      setLatitude: (latitude) => {
        set({ latitude });
      },
      setLongitude: (longitude) => {
        set({ longitude });
      },
      setPersonAddress: (address) => {
        set({ personAddress: address });
      },
      setSavedAddresses: (addresses) => {
        set({ savedAddresses: addresses });
      },
      setRestaurantId: (id) => {
        set({ restaurantId: id, locationTimestamp: Date.now() });
      },
      setRestaurantAreaId: (id) => {
        set({ restaurantAreaId: id, locationTimestamp: Date.now() });
      },
      setSelectedAreaId: (id) => {
        set({ selectedAreaId: id, locationTimestamp: Date.now() });
      },
      setInsideDeliveryArea: (inside: boolean) => {
        set({ insideDeliveryArea: inside });
      },
      setDistanceFromRestaurant: (distance: number) => {
        set({ distanceFromRestaurant: distance });
      },
      setPointOfOrder: (po) => {
        console.log("Setting Point of Order:", po);
        set({ pointOfOrder: po });
      },
      setOrderId: (id) => {
        set({ orderId: id });
      },
      setUser: (userPhone, userAddress, userNotes) => {
        set({ user: { userPhone, userAddress, userNotes } });
      },
      setMetadata: (data) => {
        set({ metadata: data });
      },
      setSignedInUserDetails: (userDetails) => {
        set({ signedInUserDetails: userDetails });
      },
      resetUserSession: () => {},
      resetRestaurant: () => {
        set({ restaurantId: "", restaurantAreaId: "" });
      },
      resetLocation: () => {
        set({
          restaurantId: "",
          restaurantAreaId: "",
          selectedAreaId: null,
          insideDeliveryArea: false,
          distanceFromRestaurant: 0,
          locationTimestamp: null,
        });
      },
      setIsOrderingEnabled: (active) => {
        set({ isOrderingEnabled: active });
      },
    }),
    {
      name: "session-store",
      storage: createSafeJSONStorage(() => localStorage),
      onRehydrateStorage: () => {
        return (rehydratedState, error) => {
          if (error) {
            console.error("Session store rehydration error:", error);
            return;
          }
          if (rehydratedState && rehydratedState.locationTimestamp) {
            const now = Date.now();
            const twoHours = 2 * 60 * 60 * 1000;
            if (now - rehydratedState.locationTimestamp > twoHours) {
              rehydratedState.resetLocation();
            }
          }
        };
      },
    }
  )
);

interface QRState {
  QRId: string;
  setQRId: (id: string) => void;
  resetQR: () => void;
}

export const useQRId = create<QRState>()(
  persist(
    (set) => ({
      QRId: "",
      setQRId: (id) => {
        set({ QRId: id });
      },
      resetQR: () => {
        localStorage.removeItem("qr-data");
        set({ QRId: "" });
      },
    }),
    { name: "qr-data", storage: createJSONStorage(() => localStorage) }
  )
);
